// Invoices API — typed-обёртки над 4 endpoints /api/invoices/* (V14 W6, §3.4; канон payments.ts).
// Контракт (backend InvoiceDto/InvoiceCreateRequest/InvoiceUpdateRequest, §3.4):
//   - GET /api/invoices?dealId=&status=&q=&page=&size= → Page<Invoice> (status — только lifecycle);
//   - POST /api/invoices: dealId обязателен; amount/currency/invoiceDate/dueDate optional
//     (null → сумма/валюта сделки, now());
//   - GET /api/invoices/{id} → InvoiceDto (404/403 — RBAC §3.5);
//   - PATCH /api/invoices/{id}: status (DRAFT→OPEN→VOID), amount/dueDate (DRAFT) + expectedUpdatedAt (409 stale).

import { apiClient } from './client';
import type { Page } from './tasks';
import type { SendEmailPayload, SendEmailResponse } from './email';

/** Хранимый жизненный цикл счёта (backend InvoiceLifecycleStatus; V37 CHECK chk_invoices_lifecycle). */
export type InvoiceLifecycleStatus = 'DRAFT' | 'OPEN' | 'VOID';

/** Вычисляемый статус счёта (backend InvoiceStatus; §3.3 — PAID/PARTIAL/OVERDUE не хранятся). */
export type InvoiceStatus = 'DRAFT' | 'OPEN' | 'PARTIAL' | 'PAID' | 'OVERDUE' | 'VOID';

/** DTO счёта (wire-формат backend InvoiceDto §3.4; имена привязанных сущностей из JOIN'ов). */
export interface Invoice {
  id: string;
  /** Сквозной номер #N (BIGSERIAL, канон payment_number V27). */
  invoiceNumber: number | null;
  dealId: string;
  /** Заголовок сделки (LEFT JOIN, nullable). */
  dealTitle: string | null;
  /** Сумма счёта (≥ 0; нулевой счёт разрешён — W1-M6 «бесплатный счёт»). */
  amount: number;
  /** ISO 4217 (канон BE: USD/EUR/RUB/GBP — chk_invoices_currency_v1_list). */
  currency: string;
  /** Дата/время счёта (ISO Instant). */
  invoiceDate: string;
  /** Срок оплаты (план; nullable — «без срока»). */
  dueDate: string | null;
  /** Хранимый жизненный цикл (DRAFT/OPEN/VOID). */
  lifecycle: InvoiceLifecycleStatus;
  /** Вычисляемый статус (SSOT: сумма CONFIRMED-платежей в валюте счёта, §3.3). */
  status: InvoiceStatus;
  /** Оплачено (SUM CONFIRMED-платежей в валюте счёта). */
  paidAmount: number;
  /** Остаток (amount − paidAmount; raw — переплата → < 0, OQ-6). */
  remaining: number;
  /** Количество платежей по счёту. */
  paymentsCount: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Фильтры списка счетов (§3.4: dealId/status/q — канон payment-фильтров). */
export interface InvoiceFilters {
  page?: number;
  size?: number;
  dealId?: string;
  /** Только lifecycle-значения (DRAFT/OPEN/VOID) — computed-статусы не фильтруются (OQ-10). */
  status?: InvoiceLifecycleStatus;
  /** Поиск по номеру счёта (ILIKE по invoice_number::text, ≤100 символов — канон V13 U-38). */
  q?: string;
}

/** Тело POST /api/invoices (dealId обязателен; amount/currency → сделка, §3.4). */
export interface InvoiceCreateRequest {
  dealId: string;
  /** Сумма счёта (nullable → сумма сделки; ≥ 0). */
  amount?: number;
  /** Код валюты (nullable → валюта сделки; канон USD/EUR/RUB/GBP). */
  currency?: string;
  /** ISO Instant (nullable → now()). */
  invoiceDate?: string;
  /** ISO Instant (nullable → «без срока»). */
  dueDate?: string;
}

/** Тело PATCH /api/invoices/{id} (partial; optimistic lock через expectedUpdatedAt). */
export interface InvoiceUpdateRequest {
  /** Целевой lifecycle (DRAFT→OPEN→VOID; прочие переходы → 400, §3.4). */
  status?: InvoiceLifecycleStatus;
  /** Новая сумма (только в DRAFT — OQ-7). */
  amount?: number;
  /** Новый срок оплаты (в любом статусе). */
  dueDate?: string;
  expectedUpdatedAt: string;
}

/** Invoices API. */
export const invoicesApi = {
  /** GET /api/invoices?filters → Page<Invoice> (ORDER BY created_at DESC — §3.4). */
  async list(filters: InvoiceFilters = {}): Promise<Page<Invoice>> {
    const { data } = await apiClient.get<Page<Invoice>>('/api/invoices', { params: filters });
    return data;
  },

  /** POST /api/invoices → 201 Invoice (lifecycle=DRAFT). */
  async create(payload: InvoiceCreateRequest): Promise<Invoice> {
    const { data } = await apiClient.post<Invoice>('/api/invoices', payload);
    return data;
  },

  /** GET /api/invoices/{id} → Invoice (404/403 — RBAC §3.5). */
  async get(id: string): Promise<Invoice> {
    const { data } = await apiClient.get<Invoice>(`/api/invoices/${id}`);
    return data;
  },

  /** PATCH /api/invoices/{id} → Invoice (optimistic lock; 409 на stale version). */
  async update(id: string, payload: InvoiceUpdateRequest): Promise<Invoice> {
    const { data } = await apiClient.patch<Invoice>(`/api/invoices/${id}`, payload);
    return data;
  },

  // V15 W7 (§3.5, M-3): отправка письма по счёту — POST /api/invoices/{id}/send-email.
  /**
   * POST /api/invoices/{id}/send-email → 202 SendEmailResponse{deliveryId, status, error?}.
   * Единый контракт (M-3): сбой провайдера = status=FAILED в теле (не 5xx);
   * гварды §5 (DRAFT/нет аккаунта/rate-limit) — 400/403/404/429 из backend.
   * Optional recipientEmail/subject/body: пустые поля omit — BE рендерит шаблон.
   */
  async sendEmail(invoiceId: string, payload: SendEmailPayload): Promise<SendEmailResponse> {
    const { data } = await apiClient.post<SendEmailResponse>(`/api/invoices/${invoiceId}/send-email`, payload);
    return data;
  },
};
